import { useMemo } from 'react';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectConfigsState,
  selectNewApplicationTypeSelectionFeature,
  selectRightStartFeature,
} from '@/redux/configs';
import { selectOperationUser } from '@/redux/operation/selectors';
import {
  selectPEPQuestions,
  selectUserHolderJointAdult,
} from '@/redux/user-data';

import { DocumentSection, DocumentStatus } from '@/types/document';
import { ICBResponseCode } from '@/types/user';

import { isTestUser as checkTestUser } from '@/utils/api';
import { answeredPersonalAccount } from '@/utils/pepQuestion';
import {
  checkUserEkycStatus,
  isAdultUser,
  isArchivedUser,
  isCaymanUser,
  isExistingCustomer,
  isIndividualUser,
  isMinorUser,
  isRejectedUser,
  isRemediationCustomer,
} from '@/utils/user';
import { isAbove18, isCaymanAddress } from '@/utils/validation';

import { useAppSelector } from './redux';

type ApplicationStatuses = {
  isRejected?: boolean;
  isInfoConfirmed?: boolean;
  isAddressConfirmed?: boolean;
  isAgeRestriction?: boolean;
  isJointAdultAgeRestriction?: boolean;
  isMinorAgeRestriction?: boolean;
  isAccountTypeSelected?: boolean;
  isIDChecked?: boolean;
  isSelfieChecked?: boolean;
  isLivenessVideoRecorded?: boolean;
  isICBReady?: boolean;
  isPhoneConfirmed?: boolean;
  isThirdPartyQuestionAnswered?: boolean;
  isThirdPartyComplianceRejected?: boolean;
  isCaymanThirdPartyComplianceRejected?: boolean;
  isCaymanCountryNotSelected?: boolean;
  isApplicationTypeSelected?: boolean;
  isMinorFilledAdultDetail?: boolean;
  isMinorFilledAdultContact?: boolean;
  isMinorIDChecked?: boolean;
  isMinorInfoConfirmed?: boolean;
  isMinorEkycStarted?: boolean;
  isNonResidentMinorRejected?: boolean;
};

export function useApplicationStatuses() {
  const enabledRightStartAccount = useAppSelector(selectRightStartFeature);
  const { isoCode: localISO, shouldRejectNonResidentMinor } =
    useAppSelector(selectConfigsState);
  const user = useAppSelector(selectLoggedInUser);
  const pepQuestions = useAppSelector(selectPEPQuestions);
  const hasHolderJointAdult = useAppSelector(selectUserHolderJointAdult);
  const minorOperationUser = useAppSelector(selectOperationUser);
  const enabledNewApplicationTypeSelection = useAppSelector(
    selectNewApplicationTypeSelectionFeature,
  );
  const statuses: ApplicationStatuses = useMemo(() => {
    if (!user) return {};

    const isTestUser = checkTestUser(user);

    let isRejected = false;
    let isPhoneConfirmed = false;
    const { isIDChecked, isSelfieChecked, isLivenessVideoRecorded } =
      checkUserEkycStatus(user);
    let isInfoConfirmed = false;
    let isAddressConfirmed = false;
    let isAgeRestriction = false;
    let isJointAdultAgeRestriction = false;
    let isThirdPartyComplianceRejected = false;
    let isCaymanCountryNotSelected = false;
    let isCaymanThirdPartyComplianceRejected = false;
    let isApplicationTypeSelected = true;
    let isMinorFilledAdultDetail = false;
    let isMinorFilledAdultContact = false;
    let thirdPartyQuestionRequired = true;

    let isAccountTypeSelected = !!(user.accountTypes || []).filter(
      acc => acc.operationId === user.operationId,
    ).length;

    const {
      isAnswered: isThirdPartyQuestionAnswered,
      isPassed: isPassedThirdPartyQuestion,
    } = answeredPersonalAccount({
      user,
      pepQuestions,
    });

    if (isRejectedUser(user)) {
      isRejected = true;
    }

    if (isArchivedUser(user) && !enabledRightStartAccount) {
      if (isCaymanUser(user)) {
        if (isThirdPartyQuestionAnswered && !isPassedThirdPartyQuestion) {
          isCaymanThirdPartyComplianceRejected = true;
        } else if (isPassedThirdPartyQuestion && !isAbove18(user.birthday)) {
          isAgeRestriction = true;
        } else if (
          isPassedThirdPartyQuestion &&
          !isCaymanAddress(user?.addressDetailed)
        ) {
          isCaymanCountryNotSelected = true;
        }
      } else {
        if (!isPassedThirdPartyQuestion) {
          isThirdPartyComplianceRejected = true;
        } else if (!isAbove18(user.birthday)) {
          isAgeRestriction = true;
        }
      }
    }

    if (user.phoneNumber) {
      isPhoneConfirmed = true;
    }

    if (user.taxResidences) {
      isAddressConfirmed = true;
    }

    if (user.username || user.countryOfBirth) {
      isInfoConfirmed = true;
    }

    if (enabledNewApplicationTypeSelection) {
      thirdPartyQuestionRequired = false;
      isApplicationTypeSelected = !!user.applicationType;
    }

    // #region RightStart
    let isMinorIDChecked: boolean | undefined;
    let isMinorInfoConfirmed: boolean | undefined;
    let isMinorEkycStarted: boolean | undefined;
    let isMinorAgeRestriction: boolean | undefined;
    let isNonResidentMinorRejected: boolean | undefined;

    if (enabledRightStartAccount) {
      if (!!user.birthday && !isAbove18(user.birthday)) {
        if (isAdultUser(user)) {
          isJointAdultAgeRestriction = true;
        }
        if (isArchivedUser(user) && isIndividualUser(user)) {
          isAgeRestriction = true;
        }
      }

      if (isPhoneConfirmed) {
        if (hasHolderJointAdult && isMinorUser(user)) {
          isMinorFilledAdultDetail = true;
        } else if (isMinorUser(user) && user?.adultEmail) {
          isMinorFilledAdultContact = true;
        }
      }

      // #region Minor
      // Compute minor's ekyc statuses from the minor's operation user (used on VERIFY_MINOR_ID / minor selfie pages)
      // These are undefined when no minor operation user is present (no minor to verify)
      if (minorOperationUser) {
        isMinorIDChecked = false;
        isMinorInfoConfirmed = false;
        if (
          shouldRejectNonResidentMinor &&
          minorOperationUser.residenceCountry &&
          minorOperationUser.residenceCountry !== localISO
        ) {
          isNonResidentMinorRejected = true;
        }

        if (
          isAbove18(minorOperationUser.birthday) &&
          minorOperationUser.adultConfirmKyc
        ) {
          isMinorAgeRestriction = true;
        }

        const allMinorDocuments = minorOperationUser.documents ?? [];
        isMinorEkycStarted = allMinorDocuments.some(
          doc => doc.type === DocumentSection.Identity,
        );

        const activeMinorDocuments = allMinorDocuments.filter(
          item => item.status !== DocumentStatus.Rejected,
        );
        for (const item of activeMinorDocuments) {
          if (item.type === DocumentSection.Identity && item.uri) {
            isMinorIDChecked = true;
          }
        }
        if (minorOperationUser.username || minorOperationUser.countryOfBirth) {
          isMinorInfoConfirmed = true;
        }
      }
      // #endregion Minor
    }
    // #endregion RightStart

    let restStatuses = {
      isIDChecked,
      isSelfieChecked,
      isLivenessVideoRecorded,
      isICBReady: isMinorUser(user)
        ? true
        : user.icbResponseCode === ICBResponseCode.Success,
    };

    if (isTestUser) {
      restStatuses = {
        isIDChecked: true,
        isSelfieChecked: true,
        isLivenessVideoRecorded: true,
        isICBReady: true,
      };
    }

    if (isExistingCustomer(user)) {
      isInfoConfirmed = true;
      isAddressConfirmed = true;
      isAgeRestriction = false;
      if (isCaymanUser(user)) {
        isPhoneConfirmed = true;
      }
      restStatuses = {
        isIDChecked: true,
        isSelfieChecked: true,
        isLivenessVideoRecorded: true,
        isICBReady: true,
      };
      // Once EXC user has documents, we check which doc is missing to decide correct navigation
      if (
        user.documents.length &&
        user.documents.some(
          doc =>
            doc.type === DocumentSection.Identity ||
            doc.type === DocumentSection.Selfie ||
            doc.type === DocumentSection.IDVideo,
        )
      ) {
        restStatuses = {
          ...checkUserEkycStatus(user),
          isICBReady: true,
        };
      }

      if (!!user.birthday && !isAbove18(user.birthday)) {
        if (enabledRightStartAccount) {
          isJointAdultAgeRestriction = true;
        } else {
          isAgeRestriction = true;
        }
      }
    }

    if (isRemediationCustomer(user)) {
      restStatuses = {
        isIDChecked: true,
        isSelfieChecked: true,
        isLivenessVideoRecorded: true,
        isICBReady: true,
      };
      isInfoConfirmed = true;
      isAddressConfirmed = true;
      isAgeRestriction = false;
      isPhoneConfirmed = true;
      thirdPartyQuestionRequired = false;
      isJointAdultAgeRestriction = false;
      isAccountTypeSelected = true;
      isApplicationTypeSelected = true;
    }

    return {
      isRejected,
      isPhoneConfirmed,
      isInfoConfirmed,
      isAddressConfirmed,
      isAgeRestriction,
      isJointAdultAgeRestriction,
      isMinorAgeRestriction,
      isThirdPartyQuestionAnswered: thirdPartyQuestionRequired
        ? isThirdPartyQuestionAnswered
        : true,
      isThirdPartyComplianceRejected,
      isCaymanCountryNotSelected,
      isCaymanThirdPartyComplianceRejected,
      isApplicationTypeSelected,
      isMinorFilledAdultDetail,
      isMinorFilledAdultContact,
      isMinorIDChecked,
      isMinorInfoConfirmed,
      isMinorEkycStarted,
      isNonResidentMinorRejected,
      /*
       * A user can have many account types from API, but to decide if user can go to Account Type Selection page,
       * we need to check if the user has selected the account type that is associated with the operationId
       */
      isAccountTypeSelected,
      ...restStatuses,
    };
  }, [
    user,
    pepQuestions,
    enabledRightStartAccount,
    hasHolderJointAdult,
    minorOperationUser,
    enabledNewApplicationTypeSelection,
    localISO,
    shouldRejectNonResidentMinor,
  ]);

  return statuses;
}
